import { z } from "zod";
import {
  localizedCategorySeoSchema,
  localizedTextSchema,
  requiredLocalizedTextSchema,
  timestampsSchema,
} from "./common";
import { listQueryBaseSchema, queryBoolean } from "./list-query";
import { mediaDtoSchema } from "./media";
import { CATEGORY_STATUS } from "../constants/status";

const statusValues = Object.values(CATEGORY_STATUS) as [number, ...number[]];

const categoryStatusSchema = z.union([
  z.literal(statusValues[0]),
  ...statusValues.slice(1).map((v) => z.literal(v)),
]);

export const categoryDtoSchema = z
  .object({
    id: z.string().uuid(),
    parentId: z.string().uuid().nullable().optional(),
    parent: z
      .object({ id: z.string().uuid(), name: localizedTextSchema })
      .nullable()
      .optional(),
    name: localizedTextSchema,
    description: localizedTextSchema,
    slug: z.string().nullable(),
    textColor: z.string().nullable(),
    backgroundColor: z.string().nullable(),
    places: z.number().int().nonnegative(),
    status: categoryStatusSchema,
    showRelatedName: z.boolean(),
    heroMedia: mediaDtoSchema.nullable(),
    /**
     * Identifier of the per-category content-block schema (e.g. "experience").
     * `null` means the category has no custom blocks.
     */
    template: z.string().nullable().optional(),
    seo: localizedCategorySeoSchema.optional(),
  })
  .extend(timestampsSchema.shape);
export type CategoryDTO = z.infer<typeof categoryDtoSchema>;

export const categoryUpsertSchema = z.object({
  name: requiredLocalizedTextSchema,
  description: localizedTextSchema,
  slug: z.string().optional(),
  textColor: z.string().optional(),
  backgroundColor: z.string().optional(),
  places: z.number().int().nonnegative().default(0),
  status: categoryStatusSchema,
  showRelatedName: z.boolean().default(false),
  /**
   * Per-category content-block template id (`"experience"`, etc.). Pass
   * `null` or omit to clear. Validated against `templateRegistry` keys
   * server-side.
   */
  template: z.string().nullable().optional(),
  seo: localizedCategorySeoSchema.optional(),
});
export type CategoryUpsertInput = z.infer<typeof categoryUpsertSchema>;

export const categoriesListQuerySchema = listQueryBaseSchema.extend({
  status: z.coerce.number().int().optional(),
  showRelatedName: queryBoolean(),
});
export type CategoriesListQuery = z.infer<typeof categoriesListQuerySchema>;
